import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';
import { NavLink, useFetcher, useSubmit } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  DiffableForm,
  HiddenInput,
  ValidatedField,
  ValidatedInput,
} from '~/components';
import { GoogleIcon } from '~/components/GoogleIcon';
import LoginHero from '~/components/LoginHero';
import { Button } from '~/components/ui/button';

import type { ActionData } from './_index';

const validator = withZod(
  z.object({
    email: z
      .string()
      .min(1, { message: 'Please provide your email' })
      .email({ message: 'E-mail address not found. Try again' }),
    password: z.string(),
  }),
);

type Props = {
  actionData?: ActionData['validationErrors'];
};

const LoginView = ({ actionData }: Props) => {
  const fetcher = useFetcher();
  const submit = useSubmit();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (actionData?.fieldErrors) {
      setStep(1);
    }
  }, [actionData]);

  return (
    <div className="h-full w-full overflow-hidden md:flex">
      <div className="align-center z-50 flex h-full w-full flex-col justify-between bg-neutral-100 px-8 pt-48 pb-6 md:w-1/4 md:min-w-[300px]">
        <div className="flex flex-col items-center justify-center">
          <p className="mb-12 text-lg leading-normal font-bold text-neutral-600">
            Welcome back
          </p>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              fetcher.submit(
                {},
                {
                  method: 'post',
                  action: '/auth/google',
                },
              );
            }}
            className="h-12 w-full"
          >
            <GoogleIcon />
            Log in with Google
          </Button>
          <div className="flex w-full items-center gap-4 py-8 text-xs font-medium">
            <div className="from-border h-px w-full bg-linear-to-l to-transparent" />
            or
            <div className="from-border h-px w-full bg-linear-to-r to-transparent" />
          </div>
          <DiffableForm
            id="login"
            validator={validator}
            method="post"
            className="flex w-full flex-col gap-4"
            CustomSubmit={({ disabled }: { disabled: boolean }) => (
              <>
                {step === 2 ? (
                  <div className="mt-auto flex w-full flex-row justify-between gap-8">
                    <Button
                      type="submit"
                      variant="secondary"
                      className="w-full"
                      disabled={disabled}
                    >
                      Log in
                      <ArrowRightOnRectangleIcon className="w-5 text-neutral-600" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-auto flex w-full flex-row justify-between gap-8">
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                      onClick={() => setStep(2)}
                      disabled={email.length === 0}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          >
            {step === 1 ? (
              <ValidatedField
                name="email"
                label="Business e-mail"
                className="w-full"
                onKeyDown={(e) => e.key === 'Enter' && setStep(2)}
                onChange={(e) => setEmail(e.target.value)}
              >
                <ValidatedInput
                  placeholder="Enter your business e-mail here..."
                  className="w-full"
                  autoComplete="email"
                  defaultValue={actionData?.repopulateFields.email}
                />
              </ValidatedField>
            ) : (
              <>
                <HiddenInput name="email" value={email} />
                <ValidatedField
                  name="password"
                  label="Password"
                  className="w-full"
                  type={showPassword ? 'text' : 'password'}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && submit(e.currentTarget.form)
                  }
                >
                  <ValidatedInput
                    placeholder="Enter your password..."
                    RightIcon={showPassword ? EyeSlashIcon : EyeIcon}
                    rightIconClickHandler={() => setShowPassword(!showPassword)}
                    autoComplete="current-password"
                    defaultValue={actionData?.repopulateFields.password}
                  />
                </ValidatedField>

                <div className="flex flex-col justify-end">
                  <NavLink to="/reset">
                    <p className="text-xxs text-accent-blue-600 leading-normal font-medium">
                      I forgot my password
                    </p>
                  </NavLink>
                </div>
              </>
            )}
          </DiffableForm>
          <p className="mt-6 text-xs leading-normal">
            New to Raffle?{' '}
            <a
              href="/signup"
              className="text-accent-blue-600 decoration-dotted hover:underline"
            >
              Create an account
            </a>
          </p>
        </div>
        <span className="mx-auto mt-auto text-neutral-500">
          &copy; raffle.ai
        </span>
      </div>

      <LoginHero />
    </div>
  );
};

export default LoginView;
